<script setup>
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'
import { addAttributes } from '../modules/filterModalConst'
import { camelCase } from '../modules/string_converter'
import { useI18n } from 'vue-i18n'
import { useSnsAccountsStore } from '../stores/companies/sns/accounts'
import {
  KkButton,
  KkLoading,
  KkApprovalSummaryItem,
} from '../components/base'
import {
  KkFilterButton
} from '../components/ui'
import {
  KkUpperTabLayout,
  KkFilterModal
} from '../components/projects'
import {
  KkCard,
  KkApprovalCard,
  KkPagination,
  KkRequiredSnsConnection
} from '../components/domains/sns'

const store = useStore()
const route = useRoute()
const { t } = useI18n()
const snsAccountsStore = useSnsAccountsStore()
const {
  loading: snsAccountsLoading,
  data: snsAccounts,
} = storeToRefs(snsAccountsStore)
const { readSnsAccounts } = snsAccountsStore
const { company_uuid } = route.params

const categoryLoading = computed(() => store.getters['companiesSnsCategories/loading'])
const displayNumber = computed(() => store.getters['filters/displayNumber'])
const posts = computed(() => store.getters['companiesStoreSnsPosts/posts'])
const postCounts = computed(() => store.getters['companiesStoreSnsPosts/postCounts'])
const searchAttributes = computed(() => store.getters['companiesSearchTagGroups/searchAttributes'])
const formValues = computed(() => store.getters['companiesStoreSnsPosts/formValues'])
const snsPostsLoading = computed(() => store.getters['companiesStoreSnsPosts/postLoading'])
const snsPostCountsLoading = computed(() => store.getters['companiesStoreSnsPosts/countLoading'])
const snsPostsMeta = computed(() => store.getters['companiesStoreSnsPosts/meta'])
const filteredCount = computed(() => store.getters['companiesStoreSnsPosts/filteredCount'])
const approvalInitMeta = computed(() => store.getters['companiesStoreSnsPosts/approvalInitMeta'])
const selectedApprovalStatusType = computed(() => store.getters['companiesStoreSnsPosts/selectedApprovalStatusType'])
const hasAvailableSnsApprovalFlow = computed(() => store.getters['companiesCompany/hasAvailableSnsApprovalFlow'])
const postStatusTypes = computed(() => store.getters['companiesStoreSnsPosts/postStatusTypes'])
const selectedPostStatusType = computed(() => store.getters['companiesStoreSnsPosts/selectedPostStatusType'])
const canCreatePostAuthority = computed(() => store.getters['companiesAccessibleApi/canCreatePostAuthority'])

const loading = computed(() => snsPostsLoading.value || snsPostCountsLoading.value || snsAccountsLoading.value || categoryLoading.value)
const showFilterModal = ref(false)
const currentPage = ref(Number(route.query['page']) || 1)
const filterItems = ref([])
const noLinked = ref(false)

const readSnsCategories = () => store.dispatch('companiesSnsCategories/readSnsCategories')
const readPostCounts = () => store.dispatch('companiesStoreSnsPosts/readPostCounts')
const resetPosts = () => store.dispatch('companiesStoreSnsPosts/resetPosts')
const updateSelectedPostStatusType = (key) => store.dispatch('companiesStoreSnsPosts/updateSelectedPostStatusType', key)
const readCompany = (companyUuid) => store.dispatch('companiesCompany/readCompany', companyUuid)
const setFormValues = (payload) => store.dispatch('companiesStoreSnsPosts/setFormValues', payload)
const readPosts = (payload) => store.dispatch('companiesStoreSnsPosts/readPosts', payload)
const readPostsForFilter = (payload) => store.dispatch('companiesStoreSnsPosts/readPostsForFilter', payload)
const readRequestingPosts = (payload) => store.dispatch('companiesStoreSnsPosts/readRequestingPosts', payload)
const readRequestingPostsForFilter = (payload) => store.dispatch('companiesStoreSnsPosts/readRequestingPostsForFilter', payload)
const readDraftedPosts = (payload) => store.dispatch('companiesStoreSnsPosts/readDraftedPosts', payload)
const deletePost = (payload) => store.dispatch('companiesStoreSnsPosts/deletePost', payload)
const deleteDraftedPost = (payload) => store.dispatch('companiesStoreSnsPosts/deleteDraftedPost', payload)
const deleteRequestingPost = (payload) => store.dispatch('companiesStoreSnsPosts/deleteRequestingPost', payload)
const deleteApprovalRequest = (payload) => store.dispatch('companiesStoreSnsPosts/deleteApprovalRequest', payload)
const updateSelectedApprovalStatusType = (type) => store.dispatch('companiesStoreSnsPosts/updateSelectedApprovalStatusType', type)
const setDisplayNumber = (num) => store.dispatch('filters/setDisplayNumber', num)
const readSearchAttributes = () => store.dispatch('companiesSearchTagGroups/readSearchTagGroups')

const resetPage = () => {
  currentPage.value = 1
  route.query.page = undefined
}

const menuList = computed(()=> {
  const menuList = {}

  for(const key in postStatusTypes.value) {
    if(key === 'IN_PROGRESS') {
      if(!hasAvailableSnsApprovalFlow.value) continue
    }
    const countKey = camelCase(key.toLowerCase())
    if (postCounts.value[countKey] !== undefined && postCounts.value[countKey] !== null) {
      menuList[key] = `${postStatusTypes.value[key]}（${postCounts.value[countKey]}）`
    } else {
      menuList[key] = postStatusTypes.value[key]
    }
  }
  return menuList
})

const isPublishedParams = computed(() => {
  if (selectedPostStatusType.value === 'POST') return { is_published: true }
  if (selectedPostStatusType.value === 'RESERVING') return { is_published: false }

  return null
})

const readPostsParams = computed(() => {
  const query = {
    category_ids: formValues.value.sns_categories,
    kind: formValues.value.sns,
    facebook_page_ids: formValues.value.facebook_pages,
    instagram_account_ids: formValues.value.instagram_accounts,
    twitter_profile_ids: formValues.value.twitter_profiles,
  }

  if(selectedPostStatusType.value === 'IN_PROGRESS'){
    query['request_status'] = selectedApprovalStatusType.value
  }

  const payload = {
    search: query,
    per: displayNumber.value,
    page: currentPage.value,
  }

  if (isPublishedParams.value) {
    payload.search.is_published = isPublishedParams.value.is_published
  }

  return payload
})

const clickApprovalSummary = async (key)=> {
  updateSelectedApprovalStatusType(key)
  await readRequestingPosts(readPostsParams.value)
}

const clickMenu = async (key) => {
  resetPage()
  updateSelectedPostStatusType(key)
  await readPostCounts()
  switch (key) {
  case 'POST':
    await readPosts(readPostsParams.value)
    break
  case 'RESERVING':
    await readPosts(readPostsParams.value)
    break
  case 'IN_PROGRESS':
    await readRequestingPosts(readPostsParams.value)
    break
  case 'DRAFT':
    await readDraftedPosts({ per: displayNumber.value, page: currentPage.value })
    break
  }
}
const clickDeletePost = async(id) => {
  if (confirm(t('pages.CompaniesStoreSnsPost.confirmToDelete'))) {
    await deletePost({
      companyUuid: company_uuid,
      id: id
    })
    readPostCounts()
    switch (selectedPostStatusType.value) {
    case 'POST':
      readPosts(readPostsParams.value)
      break
    case 'RESERVING':
      readPosts(readPostsParams.value)
      break
    }
  }
}

const clickPostApprovalPost = () => {
  switch (selectedPostStatusType.value) {
  case 'POST':
    readPosts(readPostsParams.value)
    readPostCounts()
    break
  case 'RESERVING':
    readPosts(readPostsParams.value)
    readPostCounts()
    break
  case 'IN_PROGRESS':
    readRequestingPosts(readPostsParams.value)
    readPostCounts()
    break
  }
}

const clickDeleteApprovalPost = async(param) => {
  if (confirm(t('pages.CompaniesStoreSnsPost.confirmToDelete'))) {
    let res
    if(param.status === 'draft'){
      res = await deleteDraftedPost(param.id)
    }

    if(['rejected', 'requesting'].includes(param.status)) {
      res = await deleteRequestingPost(param.id)
    }

    if(res) {
      readPostCounts()
      switch (selectedPostStatusType.value) {
      case 'IN_PROGRESS':
        readRequestingPosts(readPostsParams.value)
        break
      case 'DRAFT':
        readDraftedPosts({ per: displayNumber.value, page: currentPage.value })
        break
      }
    }
  }
}
const clickDeleteApprovalRequest = async(param) => {
  if (confirm(t('pages.CompaniesStorePost.confirmToDeleteRequest'))) {
    if(param.status !== 'requesting'){ return }

    let res
    res = await deleteApprovalRequest(param.id)

    if(res) {
      readRequestingPosts(readPostsParams.value)
      readPostCounts()
    }
  }
}
const goPrevPage = ()=> {
  --currentPage.value
  changePage()
}
const goNextPage = ()=> {
  ++currentPage.value
  changePage()
}
const changePage = () => {
  window.scrollTo({ top: 0 })
  readPostCounts()
  switch (selectedPostStatusType.value) {
  case 'POST':
    readPosts(readPostsParams.value)
    break
  case 'RESERVING':
    readPosts(readPostsParams.value)
    break
  case 'IN_PROGRESS':
    readRequestingPosts(readPostsParams.value)
    break
  case 'DRAFT':
    readDraftedPosts({ per: displayNumber.value, page: currentPage.value })
    break
  }
}
/**
 * 表示件数の変更
 * @param  { Object } e イベントオブジェクト
 * @return { Void }
 */
const changeDisplayNumber = (e) => {
  resetPage()
  setDisplayNumber(e.target.value)

  readPostCounts()
  switch (selectedPostStatusType.value) {
  case 'POST':
    readPosts(readPostsParams.value)
    break
  case 'RESERVING':
    readPosts(readPostsParams.value)
    break
  case 'IN_PROGRESS':
    // metaデータを取得するために絞り込みのない状態でリクエスト
    updateSelectedApprovalStatusType('all')
    readRequestingPosts(readPostsParams.value)

    updateSelectedApprovalStatusType(selectedApprovalStatusType.value)
    readRequestingPosts(readPostsParams.value)
    break
  case 'DRAFT':
    readDraftedPosts({ per: displayNumber.value, page: currentPage.value })
    break
  }
}

const formInitialValues = () => {
  return {
    facebook_pages: [],
    instagram_accounts: [],
    twitter_profiles: [],
    sns_categories: [],
    sns: []
  }
}

const handleChangeFilterModalCheckBox = ({ kind, id }) => {
  const newFormValues = Object.assign({}, formValues.value)

  if(newFormValues[kind].includes(id)) {
    newFormValues[kind] = newFormValues[kind].filter((_id) => _id !== id)
  } else {
    newFormValues[kind].push(id)
  }

  setFormValues(newFormValues)

  switch (selectedPostStatusType.value) {
  case 'POST':
    readPostsForFilter(readPostsParams.value)
    break
  case 'RESERVING':
    readPostsForFilter(readPostsParams.value)
    break
  case 'IN_PROGRESS':
    readRequestingPostsForFilter(readPostsParams.value)
    break
  }
}

const resetFilter = () => {
  showFilterModal.value = false
  setFormValues(formInitialValues())

  readPostCounts()
  switch (selectedPostStatusType.value) {
  case 'POST':
    readPosts(readPostsParams.value)
    break
  case 'RESERVING':
    readPosts(readPostsParams.value)
    break
  case 'IN_PROGRESS':
    readRequestingPosts(readPostsParams.value)
    break
  }
}
const handleResetPageAndSubmit = () => {
  resetPage()
  showFilterModal.value = false
  readPostCounts()
  switch (selectedPostStatusType.value) {
  case 'POST':
    readPosts(readPostsParams.value)
    break
  case 'RESERVING':
    readPosts(readPostsParams.value)
    break
  case 'IN_PROGRESS':
    readRequestingPosts(readPostsParams.value)
    break
  }
}

const createFilter = () => {
  const attributes = [
    { kind: 'sns', tags: [ {  name: 'Facebook', id: 'facebook'}, { name: 'Instagram', id: 'instagram'}, { name: 'X', id: 'twitter'}] },
    searchAttributes.value.sns_categories,
  ]
  if(searchAttributes.value.facebook_pages.tags.length !== 0) attributes.push(searchAttributes.value.facebook_pages)
  if(searchAttributes.value.instagram_accounts.tags.length !== 0) attributes.push(searchAttributes.value.instagram_accounts)
  if(searchAttributes.value.twitter_profiles.tags.length !== 0) attributes.push(searchAttributes.value.twitter_profiles)

  filterItems.value = attributes.map((attr) => {
    if(attr && attr.kind){
      return addAttributes(attr, attr.kind)
    }
  })
}

const load = async () => {
  resetPosts()

  await readSnsAccounts({ companyUuid: company_uuid })

  const currentTab = route.query.tab

  if(currentTab === 'reserving') {
    updateSelectedPostStatusType('RESERVING')
  }

  if(currentTab === 'in_progress') {
    updateSelectedPostStatusType('IN_PROGRESS')
  }

  if(currentTab === 'draft') {
    updateSelectedPostStatusType('DRAFT')
  }

  if (snsAccounts.value.length !== 0) {
    readCompany(company_uuid)
    readPostCounts()
    readSnsCategories()
    switch (selectedPostStatusType.value) {
    case 'POST':
      readPosts(readPostsParams.value)
      break
    case 'RESERVING':
      readPosts(readPostsParams.value)
      break
    case 'IN_PROGRESS':
      readRequestingPosts(readPostsParams.value)
      break
    case 'DRAFT':
      readDraftedPosts({ per: displayNumber.value, page: currentPage.value })
      break
    }
  } else {
    noLinked.value = true
  }

  await readSearchAttributes()
  setFormValues(formInitialValues())
  createFilter()
}

load()
</script>
<template>
  <div>
    <div v-if="noLinked">
      <KkRequiredSnsConnection />
    </div>
    <KkUpperTabLayout
      v-else
      :menu-list="menuList"
      :selected-tab-key="selectedPostStatusType"
      :menu-disabled="loading"
      @click-menu="clickMenu"
    >
      <template #nav-link>
        <KkButton
          v-if="canCreatePostAuthority"
          color="primary"
          class="button"
          :to="{ name: 'CompaniesStoreSnsPostsNew', params: { company_uuid: company_uuid }}"
          :text="$t('pages.CompaniesStoreSnsPost.createPost')"
        />
        <KkFilterModal
          :is-visible="showFilterModal"
          :filter-items="filterItems"
          :form-values="formValues"
          :total-count="filteredCount"
          @submit="handleResetPageAndSubmit"
          @reset="resetFilter"
          @change-check-box="handleChangeFilterModalCheckBox"
          @close="showFilterModal = false"
        />
      </template>
      <div v-if="selectedPostStatusType === 'POST'">
        <KkLoading v-if="loading" />
        <template v-else>
          <KkFilterButton @click="showFilterModal = !showFilterModal" />
          <KkCard
            v-for="post in posts"
            :id="post.id"
            :key="post.id"
            :status="post.status"
            :approver-name="post.approverName"
            :applicant-name="post.latestApplicantName"
            :author-name="post.authorName"
            :category="post.category"
            :title="post.note.title"
            :images="post.images"
            :movie="post.movie"
            :published-at="post.publishedAt? new Date(post.publishedAt) : null"
            :scheduled-at="post.note.scheduledAt ? new Date(post.note.scheduledAt) : null"
            :post-approval-flow="hasAvailableSnsApprovalFlow"
            :targets="post.targets"
            :selected-post-status-type="selectedPostStatusType"
            @delete-post="clickDeletePost($event)"
          />
          <p v-if="!posts.length && !loading">
            {{ $t('pages.CompaniesStoreSnsPost.emptyDescription') }}
          </p>
          <KkPagination
            :loading="loading"
            :current-page="currentPage"
            :display-number="Number(displayNumber)"
            :posts-length="posts.length"
            :total-pages="Number(snsPostsMeta.totalPages)"
            @onChangeDisplayNumber="changeDisplayNumber($event)"
            @goPrevPage="goPrevPage"
            @goNextPage="goNextPage"
          />
        </template>
      </div>
      <div v-if="selectedPostStatusType === 'RESERVING'">
        <KkLoading v-if="loading" />
        <template v-else>
          <KkFilterButton @click="showFilterModal = !showFilterModal" />
          <KkCard
            v-for="post in posts"
            :id="post.id"
            :key="post.id"
            :status="post.status"
            :approver-name="post.approverName"
            :applicant-name="post.latestApplicantName"
            :author-name="post.authorName"
            :category="post.category"
            :title="post.note.title"
            :images="post.images"
            :movie="post.movie"
            :published-at="post.publishedAt? new Date(post.publishedAt) : null"
            :scheduled-at="post.note.scheduledAt ? new Date(post.note.scheduledAt) : null"
            :post-approval-flow="hasAvailableSnsApprovalFlow"
            :targets="post.targets"
            :selected-post-status-type="selectedPostStatusType"
            @delete-post="clickDeletePost($event)"
          />
          <KkPagination
            :loading="loading"
            :current-page="currentPage"
            :display-number="Number(displayNumber)"
            :posts-length="posts.length"
            :total-pages="Number(snsPostsMeta.totalPages)"
            @onChangeDisplayNumber="changeDisplayNumber($event)"
            @goPrevPage="goPrevPage"
            @goNextPage="goNextPage"
          />
        </template>
      </div>
      <div v-if="selectedPostStatusType === 'IN_PROGRESS'">
        <KkLoading v-if="loading" />
        <template v-else>
          <div class="requestingPostHeader">
            <KkFilterButton @click="showFilterModal = !showFilterModal" />
            <div class="approvalSummary">
              <KkApprovalSummaryItem
                :is-active="selectedApprovalStatusType === 'all'"
                type="all"
                :count="approvalInitMeta.totalCount"
                @click="clickApprovalSummary('all')"
              />
              <KkApprovalSummaryItem
                :is-active="selectedApprovalStatusType === 'requesting'"
                type="requesting"
                :count="approvalInitMeta.requestingCount"
                @click="clickApprovalSummary('requesting')"
              />
              <KkApprovalSummaryItem
                :is-active="selectedApprovalStatusType === 'rejected'"
                type="rejected"
                :count="approvalInitMeta.rejectedCount"
                @click="clickApprovalSummary('rejected')"
              />
            </div>
          </div>
          <div class="p-table__content">
            <table class="approvalTable">
              <thead>
                <tr>
                  <th style="width: 25%">
                    <span>状況</span>
                  </th>
                  <th style="width: 25%">
                    <span>投稿のタイトル</span>
                  </th>
                  <th style="width: 15%">
                    <span>投稿内容</span>
                  </th>
                  <th style="width: 25%">
                    <span>公開設定</span>
                  </th>
                  <th style="width: 10%" />
                </tr>
              </thead>
              <tbody>
                <template v-for="post in posts" :key="post.id">
                  <KkApprovalCard
                    v-if="post"
                    :id="post.id"
                    :status="post.status"
                    :last-edited-at="post.lastEditedAt"
                    :approver-name="post.approverName"
                    :applicant-name="post.latestApplicantName"
                    :editor="post.lastEditedUserName"
                    :category="post.category"
                    :title="post.note.title"
                    :images="post.images"
                    :movie="post.movie"
                    :published-at="post.publishedAt? new Date(post.publishedAt) : null"
                    :scheduled-at="post.note.scheduledAt ? new Date(post.note.scheduledAt) : null"
                    :post-approval-flow="hasAvailableSnsApprovalFlow"
                    :targets="post.targets"
                    :author-name="post.authorName"
                    @post-approval-post="clickPostApprovalPost"
                    @delete-approval-post="clickDeleteApprovalPost($event)"
                    @delete-approval-request="clickDeleteApprovalRequest($event)"
                  />
                </template>
              </tbody>
            </table>
          </div>
          <KkPagination
            :loading="loading"
            :current-page="currentPage"
            :display-number="Number(displayNumber)"
            :posts-length="posts.length"
            :total-pages="Number(snsPostsMeta.totalPages)"
            @onChangeDisplayNumber="changeDisplayNumber($event)"
            @goPrevPage="goPrevPage"
            @goNextPage="goNextPage"
          />
        </template>
      </div>
      <div v-if="selectedPostStatusType === 'DRAFT'">
        <KkLoading v-if="loading" />
        <template v-else>
          <div class="p-table__content">
            <table class="approvalTable">
              <thead>
                <tr>
                  <th style="width: 25%">
                    <span>状況</span>
                  </th>
                  <th style="width: 25%">
                    <span>投稿のタイトル</span>
                  </th>
                  <th style="width: 15%">
                    <span>投稿内容</span>
                  </th>
                  <th style="width: 25%">
                    <span>公開設定</span>
                  </th>
                  <th style="width: 10%" />
                </tr>
              </thead>
              <tbody>
                <template v-for="post in posts" :key="post.id">
                  <KkApprovalCard
                    v-if="post"
                    :id="post.id"
                    status="draft"
                    :last-edited-at="post.lastEditedAt"
                    :approver-name="post.approverName"
                    :applicant-name="post.latestApplicantName"
                    :editor="post.lastEditedUserName"
                    :category="post.category"
                    :title="post.note.title"
                    :images="post.images"
                    :movie="post.movie"
                    :published-at="post.publishedAt? new Date(post.publishedAt) : null"
                    :scheduled-at="post.note.scheduledAt ? new Date(post.note.scheduledAt) : null"
                    :post-approval-flow="hasAvailableSnsApprovalFlow"
                    :targets="post.targets"
                    :author-name="post.authorName"
                    @post-approval-post="clickPostApprovalPost"
                    @delete-approval-post="clickDeleteApprovalPost($event)"
                  />
                </template>
              </tbody>
            </table>
          </div>
          <KkPagination
            :loading="loading"
            :current-page="currentPage"
            :display-number="Number(displayNumber)"
            :posts-length="posts.length"
            :total-pages="Number(snsPostsMeta.totalPages)"
            @onChangeDisplayNumber="changeDisplayNumber($event)"
            @goPrevPage="goPrevPage"
            @goNextPage="goNextPage"
          />
        </template>
      </div>
    </KkUpperTabLayout>
  </div>
</template>
<style lang="scss" scoped>
.requestingPostHeader {
  display: flex;

  .p-filtersA {
    width: unset;
  }
}
.approvalSummary {
  margin-left: 16px;
  display: flex;
  gap: 0 16px;
}
.approvalTable {
  border-spacing: 0 8px;
}
.button {
  @media screen and (max-width: 768px) {
    margin-left: auto;
    margin-bottom: 20px;
  }
}
</style>
